import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import { Fragment, useEffect } from 'react';
import useUnitProvider from '/@/core/context/unit';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { MuiAutocomplete } from '/@/mui/autocomplete';

const MyRecommendationsFilter = ({
    onChangeFilters,
    filters,
    filterOptions,
    setSearchInput,
    fields = ['type', 'created', 'plantingDate', 'field', 'environment', 'product'],
}) => {
    const units = useUnitProvider();
    const applyFilter = (filter, value) => {
        onChangeFilters({
            ...filters,
            [filter]: value,
        });
    };

    useEffect(() => {
        if (filterOptions && Object.keys(filterOptions).length > 0) {
            const parseDate = (dateStr) => {
                const [day, month, year] = dateStr.split('/').map(Number);
                return new Date(year, month - 1, day);
            };
            filterOptions.creation = filterOptions.creation.sort(
                (firstDate, secondDate) => parseDate(secondDate) - parseDate(firstDate)
            );
            filterOptions.planting = filterOptions.planting.sort(
                (firstDate, secondDate) => parseDate(secondDate) - parseDate(firstDate)
            );

            const parseEnv = (env) => {
                return env.split('-')[0];
            };
            const cornEnvs = filterOptions.environment
                .filter((env) => env.split('-')[1] === 'corn')
                .sort((firstEnv, secondEnv) => parseEnv(secondEnv) - parseEnv(firstEnv));
            const soyEnvs = filterOptions.environment
                .filter((env) => env.split('-')[1] === 'soy')
                .sort((firstEnv, secondEnv) => parseEnv(secondEnv) - parseEnv(firstEnv));
            filterOptions.environment = cornEnvs.concat(soyEnvs);
        }
    }, [filterOptions]);

    useEffect(() => {
        onChangeFilters(filters);
    }, [filters]);

    const onClickClear = () => {
        onChangeFilters({});
        setSearchInput('');
    };
    return (
        <Grid container spacing={2} direction='column'>
            {!Object.keys(filterOptions).length ? (
                <Grid item>
                    <Skeleton variant='rectangular' width='100%' height='35px' sx={{ mt: '0px!important' }} />
                </Grid>
            ) : (
                <Fragment>
                    <Grid item>
                        <Grid container spacing={2} direction='row' alignItems='center'>
                            {fields.includes('type') && (
                                <Grid item xs={12} sm={6} md={4} lg={fields.length > 4 ? 2 : 4}>
                                    <MuiAutocomplete
                                        size='small'
                                        value={filters.type}
                                        options={
                                            filterOptions.type.map((type) => ({
                                                value: type,
                                                label:
                                                    type === 'corn'
                                                        ? _('Maíz')
                                                        : type === 'soy'
                                                          ? _('Soja')
                                                          : type === 'corn_density'
                                                            ? _('Densidad de siembra')
                                                            : _('Maleza'),
                                            })) || []
                                        }
                                        onChange={(_, option) =>
                                            applyFilter('type', (option && option.value) || undefined)
                                        }
                                        label={_('Tipo')}
                                        getOptionLabel={(option) => option.label}
                                        getOptionValue={(option) => option.value}
                                    />
                                </Grid>
                            )}
                            {fields.includes('created') && (
                                <Grid item xs={12} sm={6} md={4} lg={2}>
                                    <MuiAutocomplete
                                        size='small'
                                        value={filters.created}
                                        options={filterOptions.creation.map((creation) => ({
                                            value: creation,
                                            label: creation,
                                        }))}
                                        onChange={(_, option) =>
                                            applyFilter('created', (option && option.value) || undefined)
                                        }
                                        label={_('Fecha de creación')}
                                        getOptionLabel={(option) => option.label}
                                        getOptionValue={(option) => option.value}
                                    />
                                </Grid>
                            )}
                            {fields.includes('plantingDate') && (
                                <Grid item xs={12} sm={6} md={4} lg={fields.length > 4 ? 2 : 4}>
                                    <MuiAutocomplete
                                        size='small'
                                        value={filters.plantingDate}
                                        options={filterOptions.planting.map((planting) => ({
                                            value: planting,
                                            label: planting,
                                        }))}
                                        onChange={(_, option) =>
                                            applyFilter('plantingDate', (option && option.value) || undefined)
                                        }
                                        label={_('Fecha de siembra')}
                                        getOptionLabel={(option) => option.label}
                                        getOptionValue={(option) => option.value}
                                    />
                                </Grid>
                            )}
                            {fields.includes('field') && (
                                <Grid item xs={12} sm={6} md={4} lg={fields.length > 4 ? 2 : 4}>
                                    <MuiAutocomplete
                                        size='small'
                                        value={filters.fieldId}
                                        options={filterOptions.field || []}
                                        onChange={(_, option) =>
                                            applyFilter('fieldId', (option && option.value) || undefined)
                                        }
                                        label={_('Lote')}
                                        getOptionLabel={(option) => option.label}
                                        getOptionValue={(option) => option.value}
                                    />
                                </Grid>
                            )}
                            {fields.includes('environment') && (
                                <Grid item xs={12} sm={6} md={4} lg={2}>
                                    <MuiAutocomplete
                                        size='small'
                                        value={filters.environment}
                                        options={filterOptions.environment.map((environment) => ({
                                            value: environment.split('-')[0],
                                            label: `${
                                                units.toYield(environment.split('-')[0], environment.split('-')[1])
                                                    .localeValue
                                            } ${units.getUnit('yield', environment.split('-')[1])}`,
                                        }))}
                                        onChange={(_, option) =>
                                            applyFilter('environment', (option && option.value) || undefined)
                                        }
                                        label={_('Ambiente')}
                                        getOptionLabel={(option) => option.label}
                                        getOptionValue={(option) => option.value}
                                    />
                                </Grid>
                            )}
                            {fields.includes('product') && (
                                <Grid item xs={12} sm={6} md={4} lg={2}>
                                    <MuiAutocomplete
                                        size='small'
                                        value={filters.product}
                                        options={filterOptions.product.map((product) => ({
                                            value: product,
                                            label: product,
                                        }))}
                                        onChange={(_, option) =>
                                            applyFilter('product', (option && option.value) || undefined)
                                        }
                                        label={_('Producto')}
                                        getOptionLabel={(option) => option.label}
                                        getOptionValue={(option) => option.value}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                    {settings.app.theme !== 'expertobayer' && (
                        <Grid item>
                            <Grid
                                container
                                spacing={2}
                                direction='row'
                                justifyContent='flex-start'
                                alignItems='flex-start'
                            >
                                <Grid item xs={12}>
                                    <Button fullWidth variant='outlined' onClick={onClickClear}>
                                        {_('Limpiar filtros')}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                </Fragment>
            )}
        </Grid>
    );
};

export default MyRecommendationsFilter;
